// ABOUTME: Workspace limit overrides section for application admin (platform admin at /admin/*).
// ABOUTME: Shows plan defaults, allows inline editing of overrides, and saves to backend.

// Application admin (platform admin at /admin/*) can edit these overrides.
// Workspace admin (customer at /settings/*) sees limits as read-only and CANNOT modify them.

import { useEffect, useState, useCallback } from 'react';
import { Shield, Save, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { limitOverrideApi } from '@/services/api/adminClient';
import type { EffectiveLimitsResponse, LimitOverrideRequest, PlanLimits } from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

interface LimitOverridesSectionProps {
  workspaceId: string;
}

/** The limit fields that can be overridden, with human-readable labels. */
const LIMIT_FIELDS: { key: keyof PlanLimits; label: string }[] = [
  { key: 'max_users', label: 'Max Users' },
  { key: 'max_data_sources', label: 'Max Data Sources' },
  { key: 'max_reports_per_month', label: 'Max Reports/Month' },
  { key: 'max_messages_per_month', label: 'Max Messages/Month' },
  { key: 'max_active_investigations', label: 'Max Active Investigations' },
];

export function LimitOverridesSection({ workspaceId }: LimitOverridesSectionProps) {
  const [data, setData] = useState<EffectiveLimitsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Local override values being edited. Empty string means "no override" (revert to plan default).
  const [overrideInputs, setOverrideInputs] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await limitOverrideApi.getEffective(workspaceId);
      setData(result);
      initializeInputs(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load effective limits');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /** Set up the override input state from server data. */
  function initializeInputs(result: EffectiveLimitsResponse) {
    const inputs: Record<string, string> = {};
    for (const { key } of LIMIT_FIELDS) {
      const overrideValue = result.overrides[key];
      // If there is an active override (value exists and is not -1 sentinel), show it
      if (overrideValue !== undefined && overrideValue !== -1) {
        inputs[key] = String(overrideValue);
      } else {
        inputs[key] = '';
      }
    }
    setOverrideInputs(inputs);
    setHasChanges(false);
  }

  const handleInputChange = (key: string, value: string) => {
    setOverrideInputs((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!data) return;

    setSaving(true);
    try {
      // Build the override request. Clearing an input sets value to -2 (revert to plan default).
      // Note: -1 means "unlimited" in plan limits, so -2 is used as the clear sentinel.
      const request: LimitOverrideRequest = {};
      for (const { key } of LIMIT_FIELDS) {
        const inputValue = overrideInputs[key];
        const currentOverride = data.overrides[key];

        if (inputValue === '' || inputValue === undefined) {
          // If there was a previous override, send -2 to clear it
          if (currentOverride !== undefined && currentOverride !== -2) {
            (request as Record<string, number>)[key] = -2;
          }
        } else {
          const numValue = parseInt(inputValue, 10);
          if (!isNaN(numValue)) {
            (request as Record<string, number>)[key] = numValue;
          }
        }
      }

      const result = await limitOverrideApi.update(workspaceId, request);
      setData(result);
      initializeInputs(result);
      toast.success('Override applied successfully');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to save overrides'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (data) {
      initializeInputs(data);
    }
  };

  if (loading) {
    return (
      <Card variant="surface">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="surface">
        <div className="bg-error-light border border-error/30 rounded-sm p-4 text-error text-sm">
          {error}
        </div>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card variant="surface">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-sm bg-warning-light">
            <Shield className="w-5 h-5 text-warning" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-surface-800">
              Limit Overrides
            </h3>
            <p className="text-xs text-surface-500">
              Plan: <span className="font-medium">{data.plan_name}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              leftIcon={<RotateCcw className="w-3.5 h-3.5" />}
            >
              Discard
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            isLoading={saving}
            disabled={!hasChanges}
            leftIcon={!saving ? <Save className="w-3.5 h-3.5" /> : undefined}
          >
            Save All Changes
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-surface-200">
              <th className="text-left py-2.5 px-3 text-xs font-medium text-surface-500 uppercase tracking-wider">
                Limit
              </th>
              <th className="text-right py-2.5 px-3 text-xs font-medium text-surface-500 uppercase tracking-wider">
                Plan Default
              </th>
              <th className="text-right py-2.5 px-3 text-xs font-medium text-surface-500 uppercase tracking-wider">
                Override
              </th>
              <th className="text-right py-2.5 px-3 text-xs font-medium text-surface-500 uppercase tracking-wider">
                Effective
              </th>
            </tr>
          </thead>
          <tbody>
            {LIMIT_FIELDS.map(({ key, label }) => {
              const planDefault = data.plan_limits[key];
              const effectiveValue = data.effective[key];
              const inputValue = overrideInputs[key] || '';
              const isOverridden = inputValue !== '';

              return (
                <tr
                  key={key}
                  className="border-b border-surface-100 last:border-b-0"
                >
                  <td className="py-2.5 px-3 text-surface-700">
                    {label}
                  </td>
                  <td className="py-2.5 px-3 text-right text-surface-500">
                    {planDefault}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <input
                      type="number"
                      min="0"
                      value={inputValue}
                      onChange={(e) => handleInputChange(key, e.target.value)}
                      placeholder="-"
                      className={`w-24 ml-auto text-right px-2 py-1 rounded-sm border text-sm ${
                        isOverridden
                          ? 'border-primary-300 bg-primary-50/50 text-primary-700 font-medium'
                          : 'border-surface-200 bg-surface-0 text-surface-500'
                      } focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50`}
                    />
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <span
                      className={`font-medium ${
                        isOverridden ? 'text-primary-600' : 'text-surface-800'
                      }`}
                    >
                      {isOverridden ? parseInt(inputValue, 10) || 0 : effectiveValue}
                    </span>
                    {isOverridden && (
                      <span className="ml-1.5 inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-primary-100 text-primary-600">
                        OVERRIDE
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default LimitOverridesSection;
